import { useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { AiSparkle } from "@/components/common/AiSparkle";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";

const EXAMPLES = ["4 beds near Oceana under $450K with a fenced yard", "Waterfront in Chesapeake with a garage", "Condo in VB close to JEB Little Creek", "Pool home in Richmond under $600K"];

/** Calls the AI search; the first try is free, then visitors are asked to create an account. */
export function useAiSearch(onResult) {
  const { requireAccount } = useAuth();
  const [busy, setBusy] = useState(false);
  const run = async (query) => {
    const q = query.trim();
    if (q.length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/search", { query: q });
      onResult(data?.params ?? {}, data?.summary ?? "", q);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "register_required") requireAccount(d.msg, () => run(q));
      else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { run, busy };
}

/** Zillow-style natural-language search bar with an animated AI border. */
export const AiSearchBar = ({ onResult, summary, onClear, initialQuery = "" }) => {
  const [q, setQ] = useState(initialQuery);
  const { run, busy } = useAiSearch(onResult);
  const ran = useRef(false);
  useEffect(() => {
    if (initialQuery && !ran.current) {
      ran.current = true;
      run(initialQuery);
    }
  }, [initialQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border-b border-neutral-200 bg-white px-4 py-3 sm:px-6" data-testid="ai-search-bar">
      <form onSubmit={(e) => { e.preventDefault(); run(q); }} className="ai-glow rounded-full">
        <div className="flex h-12 items-center gap-2 rounded-full bg-white pl-4 pr-1.5">
          <AiSparkle className="h-5 w-5 shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Ask AI: \u201c${EXAMPLES[0]}\u201d`} maxLength={300}
            className="min-w-0 flex-1 bg-transparent text-[15px] text-ink placeholder:text-neutral-500 focus:outline-none" data-testid="ai-search-input" />
          <button type="submit" disabled={busy || q.trim().length < 3} data-testid="ai-search-submit"
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-ink px-4 text-[13px] font-medium text-white transition-colors hover:bg-neutral-800 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />} <span className="hidden sm:inline">{busy ? "Thinking" : "Search with AI"}</span>
          </button>
        </div>
      </form>
      {summary ? (
        <div className="mt-2 flex items-center gap-2 text-[13px] text-ink" data-testid="ai-search-summary">
          <span className="ai-text font-medium">AI</span><span className="min-w-0 flex-1 truncate">{summary}</span>
          <button onClick={onClear} className="inline-flex items-center gap-1 text-neutral-500 hover:text-ink" data-testid="ai-search-clear"><X className="h-3.5 w-3.5" /> Clear</button>
        </div>
      ) : (
        <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="ai-search-examples">
          {EXAMPLES.slice(1).map((ex) => (
            <button key={ex} type="button" onClick={() => { setQ(ex); run(ex); }} className="shrink-0 rounded-full bg-neutral-100 px-3 py-1 text-[12px] text-neutral-700 hover:bg-neutral-200">{ex}</button>
          ))}
        </div>
      )}
    </div>
  );
};
